import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { getCommons, resetCommons, onboard as apiOnboard, getOverflowIds } from "./api";
import { getSessionId, consumeResetFlag, clearLocalUser } from "./session";

const CommonsCtx = createContext(null);

const USER_KEY = "commons.user.v1";

// The demo has no login. On first load the app quietly creates a per-session
// anonymous person named "You" — the visitor is inside the app instantly.
async function autoProvisionUser() {
  const sid = getSessionId();
  const suffix = sid.replace(/[^A-Za-z0-9]/g, "").slice(0, 10) || "demo";
  const res = await apiOnboard("You", `demo-${suffix}`);
  return {
    personId: res.person.id,
    name: res.person.name,
    phone: res.person.phone,
  };
}

export function CommonsProvider({ children }) {
  const [data, setData] = useState(null);
  const [user, setUser] = useState(undefined);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const d = await getCommons();
    setData(d);
    return d;
  }, []);

  // Apply an authoritative project-participation patch returned by
  // commitment mutations (see "Option D" in the payload-size arch
  // diagnostic). The server sends the mutated project + the visitor's
  // complete post-mutation pledges/invitations slice for that project.
  // We replace those slices atomically; every other row in the store
  // is preserved. This is the only way commitment mutations update
  // the store — the client never reproduces server mutation rules.
  const patchProjectParticipation = useCallback(({ personId, projectId, project, patch }) => {
    setData((prev) => {
      if (!prev) return prev;
      const nextProjects = project
        ? prev.projects.map((p) => (p.id === project.id ? project : p))
        : prev.projects;
      const otherPledges = prev.pledges.filter(
        (pl) => !(pl.personId === personId && pl.projectId === projectId)
      );
      const otherInvitations = prev.invitations.filter(
        (inv) => !(inv.inviteePersonId === personId && inv.projectId === projectId)
      );
      return {
        ...prev,
        projects: nextProjects,
        pledges: [...otherPledges, ...(patch?.pledges || [])],
        invitations: [...otherInvitations, ...(patch?.invitations || [])],
      };
    });
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      // Boot IIFE is wrapped in a single try/catch: a paused-503 on
      // any of the awaited calls below is neutralized by the
      // api.js interceptor (stall + redirect), but ANY other transient
      // failure (network, seed corruption, etc.) must not become an
      // unhandled promise rejection that crashes the whole tree.
      // On failure we simply leave `loading: true` so the existing
      // gate loader keeps rendering until either the paused redirect
      // lands or the user reloads.
      try {
        getSessionId();

        if (consumeResetFlag()) {
          try { await resetCommons(); } catch (_) { /* ignore */ }
          clearLocalUser();
          sessionStorage.clear();
        }

        let u;
        const stored = localStorage.getItem(USER_KEY);
        if (stored) {
          u = JSON.parse(stored);
        } else {
          u = await autoProvisionUser();
          localStorage.setItem(USER_KEY, JSON.stringify(u));
        }
        setUser(u);
        await refresh();
        setLoading(false);
      } catch (_) {
        // Intentionally swallowed — see comment above.
      }
    })();
  }, [refresh]);

  // When a tab regains focus, re-check the global Overflow list and
  // re-tag projects in place if an admin changed it meanwhile.
  useEffect(() => {
    const recheck = async () => {
      if (document.visibilityState !== "visible") return;
      let ids;
      try { ids = new Set(await getOverflowIds()); } catch { return; }
      setData((prev) => {
        if (!prev) return prev;
        const place = (p) => (ids.has(p.id) ? "overflow" : "main");
        if (prev.projects.every((p) => p.libraryPlacement === place(p))) return prev;
        return { ...prev, projects: prev.projects.map((p) => ({ ...p, libraryPlacement: place(p) })) };
      });
    };
    window.addEventListener("focus", recheck);
    document.addEventListener("visibilitychange", recheck);
    return () => {
      window.removeEventListener("focus", recheck);
      document.removeEventListener("visibilitychange", recheck);
    };
  }, []);

  const setCurrentUser = (u) => {
    setUser(u);
    if (u) localStorage.setItem(USER_KEY, JSON.stringify(u));
    else localStorage.removeItem(USER_KEY);
  };

  const resetAll = async () => {
    await resetCommons();
    clearLocalUser();
    // Also clear session-scoped UI dismissals (e.g. the sharing banner)
    // so a reset truly returns the demo to a first-visit state.
    sessionStorage.clear();
    const u = await autoProvisionUser();
    localStorage.setItem(USER_KEY, JSON.stringify(u));
    setUser(u);
    await refresh();
  };

  return (
    <CommonsCtx.Provider value={{ data, setData, user, setUser: setCurrentUser, refresh, patchProjectParticipation, resetAll, loading }}>
      {children}
    </CommonsCtx.Provider>
  );
}

export function useCommons() {
  const ctx = useContext(CommonsCtx);
  if (!ctx) throw new Error("useCommons outside provider");
  return ctx;
}
